import { Banner } from "../../util/Banner"
import { useTranslation } from "react-i18next"
import { useDropzone } from "react-dropzone"
import { useCallback, useEffect, useState } from "react"
import { gql, useMutation, useQuery } from "@apollo/client"
import { useHistory, useParams } from "react-router-dom"
import { Loader } from "../../util/Loader"
import { uniqueId } from "lodash"
import inputStyle from "../../../style/input.module.css"
import style from "../groups/GroupUpload.module.css"
import cn from "classnames"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import tableStyle from "../../../style/table.module.css"
import buttonStyle from "../../../style/button.module.css"
import c from "capitalize"
import { showError } from "../../../redux/actions/NotificationActions"
import { useDispatch } from "react-redux"
import downloadCsv from "js-file-download"
import { Toggle } from "../../util/input/Toggle"
import { prisoBasePath, prisoBannerKey } from "./prisoUtils"

const UPLOAD_PRISO_FILE = gql`
    mutation (
        $file: Upload!
        $name: String!
        $prisoType: String!
        $force: Boolean
        $id: UUID
    ) {
        createPrisoListFromCsv(
            file: $file
            name: $name
            prisoType: $prisoType
            force: $force
            id: $id
        ) {
            id
        }
    }
`

const GET_PRISO_LIST = gql`
    query ($id: UUID!) {
        list: prisoList(id: $id) {
            id
            name
        }
    }
`

const CSV_TEMPLATES = {
    NEUANLAGE_KETTARTIKEL:
        "gruppe;pzn;einheit;ve;jahrgang;artikelbezeichnung;alkoholgehalt;lieferant_hnr;bezug;lagerkennzeichen;bestellnummer;bestelladresse;artikelgruppe;therapiegruppe;sortiment;cluster;a_grosso_1;zusatzkosten;steuer;mwst;kettartikel_pzn",
    EK_AENDERUNG: "pzn;preis_gueltig_ab;neuer_ek;zusatzkosten_fl;sektsteuer_fl",
    SALE: "pzn;kundengruppe;rabattsatz;valid_from;valid_until",
}

const CSV_FILENAMES = {
    NEUANLAGE_KETTARTIKEL: "priso_neuanlage_kettartikel_upload.csv",
    EK_AENDERUNG: "priso_ek_aenderung_upload.csv",
    SALE: "priso_sale_upload.csv",
}

export const PrisoUpload = ({ prisoType }) => {
    const { t } = useTranslation(["enums", "table", "common", "page_names"])
    const history = useHistory()
    const { id } = useParams()

    const basePath = prisoBasePath(prisoType)
    const bannerKey = prisoBannerKey(prisoType)

    const [name, setName] = useState("")
    const [file, setFile] = useState("")
    const [force, setForce] = useState(false)

    const [name_input_id] = useState(() => uniqueId("name_input_"))
    const [ignore_missing_product_id] = useState(() =>
        uniqueId("ignore_missing_product_"),
    )

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles[0]) {
            setFile(acceptedFiles[0])
            if (!name) {
                setName(acceptedFiles[0].name || "")
            }
        }
    }, [])

    const [upload, { loading }] = useMutation(UPLOAD_PRISO_FILE)

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
    })

    const { data, loading: prisoListLoading } = useQuery(GET_PRISO_LIST, {
        variables: { id },
        skip: !id,
    })

    useEffect(() => {
        setName(data?.list?.name || "")
    }, [data])

    const dispatch = useDispatch()

    if (loading) {
        return (
            <>
                <Banner text={t(`page_names:${bannerKey}`)} />
                <Loader />
            </>
        )
    }

    return (
        <>
            <Banner text={t(`page_names:${bannerKey}`)} />

            <div className={tableStyle.stackedFilterBar}>
                {loading || (id && prisoListLoading) ? (
                    <Loader />
                ) : (
                    <div className={tableStyle.filterBar}>
                        <div className={tableStyle.filterInnerSection}>
                            <label
                                htmlFor={name_input_id}
                                className={tableStyle.label}
                            >
                                {t("table:name")}
                            </label>
                            <input
                                id={name_input_id}
                                className={inputStyle.text}
                                type="text"
                                value={name}
                                onChange={(evt) => setName(evt.target.value)}
                            />
                        </div>
                        <div className={tableStyle.filterInnerSection}>
                            <label
                                htmlFor={ignore_missing_product_id}
                                className={tableStyle.label}
                            >
                                {t("table:ignore_missing_product")}
                            </label>
                            <Toggle
                                id={ignore_missing_product_id}
                                isChecked={force}
                                setIsChecked={setForce}
                            />
                        </div>
                    </div>
                )}
                <div className={tableStyle.buttonSection}>
                    <button
                        className={buttonStyle.button}
                        disabled={!name || !file}
                        onClick={() => {
                            upload({
                                variables: {
                                    file,
                                    name,
                                    prisoType,
                                    force,
                                    id: id,
                                },
                            })
                                .then((r) => {
                                    history.push(
                                        `${basePath}/${r?.data?.createPrisoListFromCsv?.id}`,
                                    )
                                })
                                .catch((e) => dispatch(showError(e.message)))
                        }}
                    >
                        {c(t("table:upload"))}
                    </button>
                    <button
                        onClick={() =>
                            downloadCsv(
                                CSV_TEMPLATES[prisoType],
                                CSV_FILENAMES[prisoType],
                                "application/csv",
                            )
                        }
                        className={buttonStyle.button}
                    >
                        {t("table:download_template_csv")}
                    </button>
                </div>
            </div>

            {!file && (
                <div className={style.dropWrapper}>
                    <div
                        className={cn(
                            style.dropZone,
                            isDragActive && style.highlight,
                        )}
                        {...getRootProps()}
                    >
                        <input {...getInputProps()} />
                        {isDragActive ? (
                            <h2>{t("table:drop_to_upload")}</h2>
                        ) : (
                            <h2>{t("table:drag_to_upload")}</h2>
                        )}
                    </div>
                </div>
            )}
            {file && (
                <div className={style.uploadedList}>
                    <div className={style.fileBox}>
                        <p>{file?.name || t("table:file")}</p>
                        <button
                            className={cn(
                                tableStyle.actionButton,
                                tableStyle.negativeActionButton,
                            )}
                            onClick={() => setFile(null)}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>
            )}
        </>
    )
}
